/* Collection price order is based on cards, rather than Shopify's base products.
 * The small index contains no image/product payloads. Card HTML is fetched only
 * for the source pages needed by the next visible batch. */
(() => {
  if (window.VianelliVariantPriceSort) return;

  const controllers = new WeakMap();
  const indexCache = new Map();
  const PRICE_ORDERS = new Set(['price-ascending', 'price-descending']);
  const INDEX_CACHE_LIMIT = 6;
  const REQUEST_TIMEOUT = 20000;

  const gridFor = (container) => container?.querySelector('#product-grid[data-vianelli-price-sort]');
  const isActive = (container) => PRICE_ORDERS.has(gridFor(container)?.dataset.vianelliPriceSort);
  const keyFor = (card) => card.dataset.variantId || (card.dataset.productId ? `product-${card.dataset.productId}` : '');

  class VariantPriceController {
    constructor(container, options = {}) {
      this.container = container;
      this.grid = gridFor(container);
      this.order = this.grid.dataset.vianelliPriceSort;
      this.sectionId = this.grid.dataset.id;
      this.pageSize = Number(this.grid.dataset.pageSize) || 24;
      this.params = new URLSearchParams(options.searchParams ?? location.search);
      this.requestedPage = Math.max(1, Number.parseInt(this.params.get('page'), 10) || 1);
      ['page', 'view', 'section_id', 'sections'].forEach((name) => this.params.delete(name));
      this.params.set('sort_by', this.order);
      this.abortController = new AbortController();
      this.cards = new Map();
      this.loadedSourcePages = new Set();
      this.sourceRequests = new Map();
      this.currentPage = 0;
      this.entries = null;
      this.destroyed = false;
      this.collectCards(this.grid);
      // A remount may reuse a grid already arranged into display batches. Its
      // page number is then unrelated to the original Shopify source page.
      if (!this.grid.hasAttribute('data-vianelli-price-sort-ready')) {
        this.loadedSourcePages.add(Number(this.grid.dataset.currentPage) || 1);
      }
    }

    get hasNextPage() {
      return !!this.entries && this.currentPage * this.pageSize < this.entries.length;
    }

    ensureCurrent() {
      if (this.destroyed || !this.container.isConnected || gridFor(this.container) !== this.grid) {
        throw new DOMException('Collection navigation changed.', 'AbortError');
      }
    }

    requestURL(page, index = false) {
      const url = new URL(location.pathname, location.origin);
      url.search = this.params.toString();
      url.searchParams.set('page', String(page));
      if (index) url.searchParams.set('view', this.grid.dataset.vianelliIndexView || 'vianelli-price-index');
      else url.searchParams.set('section_id', this.sectionId);
      return url;
    }

    async request(url) {
      this.ensureCurrent();
      const aborter = new AbortController();
      const abort = () => aborter.abort();
      this.abortController.signal.addEventListener('abort', abort, { once: true });
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; aborter.abort(); }, REQUEST_TIMEOUT);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: aborter.signal });
        if (!response.ok) throw new Error(`Collection price order: HTTP ${response.status}`);
        const text = await response.text();
        this.ensureCurrent();
        return text;
      } catch (error) {
        if (timedOut && !this.abortController.signal.aborted) throw new Error('Collection price order timed out.');
        throw error;
      } finally {
        clearTimeout(timer);
        this.abortController.signal.removeEventListener('abort', abort);
      }
    }

    async loadIndex() {
      this.ensureCurrent();
      const cacheKey = `${location.pathname}?${this.params.toString()}|${this.pageSize}`;
      if (indexCache.has(cacheKey)) {
        this.entries = indexCache.get(cacheKey);
        return;
      }
      const entries = [];
      const seen = new Map();
      let page = 1;
      while (page !== null) {
        const data = JSON.parse(await this.request(this.requestURL(page, true)));
        if (!Array.isArray(data.variants)) throw new Error('Invalid collection price index.');
        data.variants.forEach((entry) => {
          const id = String(entry.id ?? '');
          const price = Number(entry.price);
          const productIndex = Number(entry.productIndex);
          const sourcePage = entry.productIndex !== undefined && Number.isInteger(productIndex) && productIndex >= 0
            ? Math.floor(productIndex / this.pageSize) + 1 : Number(entry.sourcePage);
          if (!id || !Number.isSafeInteger(price) || price < 0 || !Number.isInteger(sourcePage) || sourcePage < 1) {
            throw new Error('Invalid card in collection price index.');
          }
          const previous = seen.get(id);
          if (previous) {
            if (previous.price !== price || previous.sourcePage !== sourcePage) throw new Error('Conflicting cards in price index.');
            return;
          }
          const card = { id, price, sourcePage, ordinal: entries.length };
          entries.push(card);
          seen.set(id, card);
        });
        const nextPage = data.nextPage === null ? null : Number(data.nextPage);
        if (nextPage !== null && (!Number.isInteger(nextPage) || nextPage <= page || nextPage > 100)) {
          throw new Error('Invalid collection price index pagination.');
        }
        page = nextPage;
      }
      this.ensureCurrent();
      const direction = this.order === 'price-descending' ? -1 : 1;
      entries.sort((a, b) => direction * (a.price - b.price) || a.ordinal - b.ordinal);
      this.entries = entries;
      indexCache.set(cacheKey, entries);
      if (indexCache.size > INDEX_CACHE_LIMIT) indexCache.delete(indexCache.keys().next().value);
    }

    collectCards(grid) {
      // Keep script-driven initial/source batches at the same two-image contract.
      grid.querySelectorAll('noscript[data-vianelli-media-fallback]').forEach(node => node.remove());
      [...grid.children].forEach((card) => {
        if (!card.classList.contains('product-grid__item')) return;
        const key = keyFor(card);
        if (key) this.cards.set(key, card);
      });
    }

    async loadSourcePage(page) {
      if (this.loadedSourcePages.has(page)) return;
      if (this.sourceRequests.has(page)) return this.sourceRequests.get(page);
      const request = (async () => {
        const html = await this.request(this.requestURL(page));
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const grid = parsed.getElementById('product-grid');
        if (!grid) throw new Error('Collection card page is missing.');
        this.collectCards(grid);
        this.loadedSourcePages.add(page);
      })();
      this.sourceRequests.set(page, request);
      try { await request; }
      finally { this.sourceRequests.delete(page); }
    }

    async loadNeededPages(entries) {
      const pages = [...new Set(entries.filter((entry) => !this.cards.has(entry.id)).map((entry) => entry.sourcePage))];
      let cursor = 0;
      // A large collection must not create one simultaneous HTML request per product.
      const worker = async () => {
        while (cursor < pages.length) {
          const page = pages[cursor++];
          await this.loadSourcePage(page);
        }
      };
      await Promise.all(Array.from({ length: Math.min(2, pages.length) }, worker));
    }

    async loadTo(page) {
      const lastPage = Math.max(1, Math.ceil(this.entries.length / this.pageSize));
      const targetPage = Math.min(page, lastPage);
      const visible = this.entries.slice(0, targetPage * this.pageSize);
      await this.loadNeededPages(visible);
      this.ensureCurrent();
      const cards = visible.map((entry) => {
        const card = this.cards.get(entry.id);
        if (!card || Number(card.dataset.variantPriceCents) !== entry.price) {
          throw new Error('Collection card does not match its price index.');
        }
        return card;
      });
      // Validate the complete batch before replacing any visible cards.
      cards.forEach((card, index) => {
        card.dataset.page = String(Math.floor(index / this.pageSize) + 1);
        card.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'));
        card.classList.add('scroll-trigger--cancel');
        const primary = card.querySelector('img');
        if (primary && index < 4) primary.loading = 'eager';
      });
      const existing = [...this.grid.children];
      const keepExisting = this.currentPage > 0 && existing.length <= cards.length &&
        existing.every((card, index) => card === cards[index]);
      // Loading another batch must not reconnect cards already on screen: that
      // can reset keyboard focus and repeat custom-element initialization.
      if (!keepExisting) this.grid.replaceChildren();
      cards.slice(keepExisting ? existing.length : 0).forEach((card) => this.grid.append(card));
      this.currentPage = targetPage;
      this.grid.dataset.currentPage = String(targetPage);
      this.grid.dataset.totalPages = String(lastPage);
      this.grid.dataset.lastPage = String(lastPage);
      this.grid.dataset.totalItems = String(this.entries.length);
      this.grid.setAttribute('data-vianelli-price-sort-ready', '');
      this.grid.removeAttribute('aria-busy');
      this.updatePagination();
      this.status.hidden = this.entries.length > 0;
      if (!this.entries.length) {
        this.status.textContent = 'Nenhum produto disponível com estes filtros.';
        this.pagination.hidden = true;
      }
      this.updateHistory();
      document.dispatchEvent(new CustomEvent('productsLoaded', { detail: {
        page: targetPage, totalPages: lastPage, hasNextPage: this.hasNextPage
      } }));
    }

    createUI() {
      this.status = document.createElement('div');
      this.status.className = 'vianelli-price-sort-status';
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.grid.before(this.status);
      const oldPagination = this.container.querySelector('.pagination-wrapper');
      if (oldPagination) oldPagination.hidden = true;
      this.pagination = document.createElement('div');
      this.pagination.className = 'pagination-wrapper infinite-scrolling-wrapper block center';
      this.pagination.setAttribute('data-vianelli-price-pagination', '');
      this.progress = document.createElement('p');
      this.progress.className = 'pagination-page-item pagination-page-total';
      this.progress.setAttribute('aria-live', 'polite');
      this.moreButton = document.createElement('button');
      this.moreButton.type = 'button';
      this.moreButton.className = 'button button--primary infinite-scrolling-btn';
      this.moreButton.textContent = 'Mostrar mais';
      this.moreButton.addEventListener('click', () => this.loadMore());
      this.pagination.append(this.progress, this.moreButton);
      this.grid.closest('#ResultsList')?.after(this.pagination);
      if (!this.pagination.isConnected) this.grid.after(this.pagination);
      this.pagination.hidden = true;
    }

    setLoading(initial = false) {
      if (initial) {
        this.grid.setAttribute('aria-busy', 'true');
        this.status.hidden = false;
        this.status.textContent = 'Organizando produtos por preço…';
      }
      this.moreButton.disabled = true;
      this.moreButton.textContent = 'Carregando…';
    }

    updatePagination() {
      this.pagination.hidden = false;
      this.progress.textContent = `${this.grid.children.length} de ${this.entries.length} opções`;
      this.moreButton.hidden = !this.hasNextPage;
      this.moreButton.disabled = false;
      this.moreButton.textContent = 'Mostrar mais';
      if (this.hasNextPage && !this.observer && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
        }, { rootMargin: '-100px', threshold: 0.1 });
        this.observer.observe(this.moreButton);
      }
      if (!this.hasNextPage) this.observer?.disconnect();
    }

    updateHistory() {
      const url = new URL(location.pathname, location.origin);
      url.search = this.params.toString();
      if (this.currentPage > 1) url.searchParams.set('page', String(this.currentPage));
      history.replaceState({ ...(history.state || {}), searchParams: url.searchParams.toString() }, '', url);
      window.dispatchEvent(new CustomEvent('vianelli:collection-page', {
        detail: { searchParams: url.searchParams.toString(), page: this.currentPage }
      }));
    }

    showError(error, retry) {
      if (error.name === 'AbortError' || this.destroyed) return;
      this.grid.removeAttribute('aria-busy');
      this.status.hidden = false;
      this.status.textContent = 'Não foi possível organizar os produtos por preço. ';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.textContent = 'Tentar novamente';
      button.addEventListener('click', retry, { once: true });
      this.status.append(button);
      this.moreButton.disabled = false;
      this.moreButton.textContent = 'Mostrar mais';
      console.error('Unable to organize collection variant prices.', error);
    }

    async start() {
      if (!this.status) this.createUI();
      this.setLoading(true);
      try {
        if (!this.entries) await this.loadIndex();
        await this.loadTo(this.requestedPage);
      } catch (error) {
        this.showError(error, () => { this.ready = this.start(); });
      }
      return this;
    }

    loadMore() {
      if (this.destroyed || !this.hasNextPage) return Promise.resolve();
      if (this.loading) return this.loading;
      this.setLoading();
      this.loading = this.loadTo(this.currentPage + 1)
        .catch((error) => this.showError(error, () => this.loadMore()))
        .finally(() => { this.loading = null; });
      return this.loading;
    }

    destroy() {
      this.destroyed = true;
      this.abortController.abort();
      this.observer?.disconnect();
      this.cards.clear();
      this.sourceRequests.clear();
    }
  }

  const destroy = (container) => {
    controllers.get(container)?.destroy();
    controllers.delete(container);
  };
  const mount = (container, options = {}) => {
    if (!container || !isActive(container)) {
      if (container) destroy(container);
      return Promise.resolve(null);
    }
    const previous = controllers.get(container);
    if (previous && previous.grid === gridFor(container) && !previous.destroyed) return previous.ready;
    destroy(container);
    const controller = new VariantPriceController(container, options);
    controllers.set(container, controller);
    controller.ready = controller.start();
    return controller.ready;
  };
  window.VianelliVariantPriceSort = { mount, destroy, isActive, controller: (container) => controllers.get(container) };
  const boot = () => mount(document.getElementById('ProductGridContainer'));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
